import type { DiscountDTO, Option, QliroOrderItem, StorefrontOrderLine } from 'ecosystem'
import {
  type BuyerVatRates,
  convertMinorUnitToValue,
  getVatRateByCountryCode,
  shippingForBuyer,
  storeGrossRebaser,
  sumArrayOfNumbers
} from 'shared-utils'
import { orderLineValueCalculator } from '../../payment'

/**
 * Qliro uses decimal prices (e.g. 99.99), NOT minor units (9999).
 * So we must convert from minor units (used internally) to decimals.
 */

const mapOrderLinesToQliroOrderItems = (
  items: StorefrontOrderLine[],
  cartDiscountCode: DiscountDTO | null
): QliroOrderItem[] => {
  return items
    .map((item) => {
      const { product, quantity, vatRate: itemVatRate } = item
      const { unitPrice, totalDiscountAmount } = orderLineValueCalculator(item, cartDiscountCode)

      const priceIncVat = convertMinorUnitToValue(unitPrice)
      const priceExVat = priceIncVat / (1 + itemVatRate / 100)

      const orderItem: QliroOrderItem = {
        MerchantReference: product.articleNumber,
        Description: product.name,
        Type: 'Product',
        Quantity: quantity,
        PricePerItemIncVat: priceIncVat,
        PricePerItemExVat: Math.round(priceExVat * 100) / 100,
        VatRate: itemVatRate
      }

      // If there's a discount, add a separate discount line
      if (totalDiscountAmount > 0) {
        return [
          orderItem,
          {
            MerchantReference: `discount-${product.articleNumber}`,
            Description: `Discount: ${product.name}`,
            Type: 'Discount' as const,
            Quantity: 1,
            PricePerItemIncVat: -convertMinorUnitToValue(totalDiscountAmount),
            PricePerItemExVat:
              Math.round(
                (-convertMinorUnitToValue(totalDiscountAmount) / (1 + itemVatRate / 100)) * 100
              ) / 100,
            VatRate: itemVatRate
          }
        ]
      }

      return [orderItem]
    })
    .flat()
}

const servicesOrderLineFactory = (
  items: StorefrontOrderLine[],
  vatRate: number,
  rebaseServicePrice: (grossMajor: number) => number
): QliroOrderItem[] => {
  return items
    .map((item) =>
      item.services.map((service) => {
        const quantity = service.quantity
        let unitPrice = rebaseServicePrice(service.service.price)
        const discountFactor = rebaseServicePrice(service.service.discountPrice)
        let totalAmount = 0

        if (quantity > 0) {
          totalAmount = unitPrice + (quantity - 1) * discountFactor

          if (!unitPrice && discountFactor) {
            unitPrice = discountFactor
          }
        }

        const pricePerItemIncVat = quantity > 0 ? totalAmount / quantity : 0
        const pricePerItemExVat = pricePerItemIncVat / (1 + vatRate / 100)

        return {
          MerchantReference: service.service.id,
          Description: service.service.name,
          Type: 'Fee' as const,
          Quantity: quantity,
          PricePerItemIncVat: Math.round(pricePerItemIncVat * 100) / 100,
          PricePerItemExVat: Math.round(pricePerItemExVat * 100) / 100,
          VatRate: vatRate
        } satisfies QliroOrderItem
      })
    )
    .flat()
}

export const qliroOrderLineFactory = (
  items: StorefrontOrderLine[],
  cartDiscountCode: DiscountDTO | null,
  countryCode?: string,
  buyerVat?: Option<BuyerVatRates>
) => {
  // Product rows carry each line's own `vatRate`, already resolved for the
  // buyer upstream. Service rows have no rate of their own, so they take the
  // store's rate — resolved here for the same buyer so the two agree.
  const domesticRate = getVatRateByCountryCode(countryCode)
  const vatRate = buyerVat?.resolveRate(domesticRate) ?? domesticRate
  const productOrderLines = mapOrderLinesToQliroOrderItems(items, cartDiscountCode)
  const serviceOrderLines = servicesOrderLineFactory(
    items,
    vatRate,
    storeGrossRebaser(domesticRate, buyerVat)
  )
  const orderLines = [...productOrderLines, ...serviceOrderLines]
  const orderAmount = sumArrayOfNumbers(
    orderLines.map((item) => item.PricePerItemIncVat * item.Quantity)
  )
  // The same basket net of VAT, in the units `orderAmount` uses. Feeds
  // `netOrderAmount` on the shipping factories so a delivery rate configured
  // ex-VAT is evaluated against the figure it was written on. Derived from the
  // rows rather than from the basket, so the discount code and this provider's
  // unit convention are already applied.
  const netOrderAmount = sumArrayOfNumbers(
    orderLines.map((item) => item.PricePerItemExVat * item.Quantity)
  )
  return {
    orderLines,
    productOrderLines,
    orderAmount,
    netOrderAmount,
    vatRate,
    /** Move a carrier's VAT-inclusive quote onto this buyer's rate. Unit-agnostic
     *  (a pure ratio), so minor units pass through unchanged. */
    rebaseShipping: (grossQuote: number) =>
      shippingForBuyer(grossQuote, domesticRate, buyerVat).cost
  }
}
